import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/* Impresión de cotización — solo formato A4, según modelo Pro-00452.pdf */

const fmt2 = (n) => parseFloat(n || 0).toFixed(2);
const fmtMiles = (n) =>
  (Number(n) || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtFecha = (d) => {
  if (!d) return '—';
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, dia] = s.split('-');
  return `${dia}/${m}/${y}`;
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const logoUrl = (empresa) => (empresa?.logo ? `${API}${empresa.logo}` : null);

const CSS_GLOBAL = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4; margin: 0; }

  body.fmt-cot-a4 {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9.5pt;
    color: #111;
    background: #fff;
  }

  /* ── Encabezado: logo izquierda + caja número derecha ── */
  .cotp-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 8px;
  }
  .cotp-logo { max-width: 150px; max-height: 85px; display: block; }
  .cotp-logo-placeholder { width: 150px; }
  .cotp-doc-box {
    border: 1.5px solid #222;
    border-radius: 4px;
    text-align: center;
    padding: 8px 14px;
    min-width: 190px;
  }
  .cotp-doc-tipo { font-size: 11pt; font-weight: bold; text-transform: uppercase; line-height: 1.25; }
  .cotp-doc-num  { font-size: 11pt; font-weight: bold; margin-top: 5px; letter-spacing: 1px; }

  /* ── Caja empresa (borde redondeado, centrada) ── */
  .cotp-empresa-box {
    border: 1.5px solid #222;
    border-radius: 10px;
    text-align: center;
    padding: 8px 12px;
    margin-bottom: 8px;
  }
  .cotp-empresa-nombre { font-size: 12pt; font-weight: bold; text-transform: uppercase; }
  .cotp-empresa-linea  { font-size: 8.5pt; color: #333; margin-top: 2px; }

  /* ── Franja info (borde punteado) ── */
  .cotp-info {
    border: 1px dashed #666;
    padding: 6px 10px;
    margin-bottom: 8px;
    font-size: 9pt;
  }
  .cotp-info-row  { display: flex; gap: 24px; padding: 2px 0; }
  .cotp-info-item { display: flex; gap: 6px; align-items: baseline; }
  .cotp-info-key  { font-weight: bold; }

  /* ── Tabla ── */
  .cotp-tabla { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
  .cotp-tabla th {
    border: 1px solid #222;
    background: #efefef;
    padding: 4px 6px;
    font-size: 8.5pt;
    font-weight: bold;
    text-align: center;
  }
  .cotp-tabla td {
    border-left: 1px solid #999;
    border-right: 1px solid #999;
    border-bottom: 1px dotted #bbb;
    padding: 3px 6px;
    vertical-align: top;
  }
  .cotp-tabla tbody tr:last-child td { border-bottom: 1px solid #222; }
  .ctr { text-align: center; }
  .num { text-align: right; }

  /* ── Resumen: productos + total ── */
  .cotp-resumen {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 6px 2px 4px;
    font-size: 10pt;
  }
  .cotp-productos { font-weight: bold; text-decoration: underline; }
  .cotp-totales { min-width: 220px; font-size: 9.5pt; }
  .cotp-tot-row { display: flex; justify-content: space-between; padding: 1px 4px; }
  .cotp-tot-row.final {
    font-weight: bold;
    font-size: 11.5pt;
    border-top: 1.5px solid #222;
    margin-top: 3px;
    padding-top: 3px;
  }

  /* ── V°B° ── */
  .cotp-vobo {
    text-align: right;
    margin: 34px 30px 20px 0;
    font-size: 9pt;
  }
  .cotp-vobo-line  { display: inline-block; border-top: 1px solid #222; padding: 3px 40px 0; font-weight: bold; }

  /* ── Pie: 3 columnas ── */
  .cotp-footer {
    display: flex;
    gap: 12px;
    border-top: 1.5px solid #222;
    padding-top: 6px;
    font-size: 7.5pt;
    color: #333;
  }
  .cotp-footer-col   { flex: 1; }
  .cotp-footer-title { font-weight: bold; font-size: 8pt; text-transform: uppercase; margin-bottom: 3px; }
  .cotp-footer-col div { margin-top: 1px; }

  .cotp-anulada-marca {
    text-align: center;
    color: #b91c1c;
    border: 2px solid #b91c1c;
    font-weight: bold;
    font-size: 13pt;
    padding: 4px;
    margin-bottom: 8px;
    letter-spacing: 3px;
  }

  @media screen {
    body { background: #d0d0d0 !important; }
    .cotp-hoja {
      background: #fff;
      margin: 20px auto;
      padding: 20px;
      max-width: 210mm;
      box-shadow: 0 2px 12px rgba(0,0,0,.3);
    }
  }
  @media print {
    body { background: #fff !important; }
    .cotp-hoja { box-shadow: none !important; margin: 0 !important; padding: 12mm 15mm !important; }
  }
`;

export default function CotizacionImprimirPage() {
  const { id } = useParams();
  const [cot, setCot] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);

  useEffect(() => {
    document.body.className = 'fmt-cot-a4';
    return () => { document.body.className = ''; };
  }, []);

  useEffect(() => {
    Promise.all([
      api.get(`/api/cotizaciones/${id}`),
      api.get('/api/empresa'),
    ])
      .then(([rc, re]) => {
        const c = rc.data?.data || rc.data;
        if (c?.numero) document.title = c.numero;
        setCot(c);
        setEmpresa(re.data?.empresa || re.data?.data || re.data);
        setListo(true);
      })
      .catch(() => setError('No se pudo cargar la cotización.'));
  }, [id]);

  useEffect(() => {
    if (!listo) return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [listo]);

  if (error) return <><style>{CSS_GLOBAL}</style><div style={{ padding: 20, color: 'red' }}>{error}</div></>;
  if (!listo) return <><style>{CSS_GLOBAL}</style><div style={{ padding: 20 }}>Cargando cotización...</div></>;

  const lineas = cot.lineas || [];
  const nroCorto = (cot.numero || '').split('-').pop();

  return (
    <>
      <style>{CSS_GLOBAL}</style>
      <div className="cotp-hoja">

        {/* Encabezado: logo + caja número */}
        <div className="cotp-header">
          {logoUrl(empresa)
            ? <img src={logoUrl(empresa)} alt="logo" className="cotp-logo" />
            : <div className="cotp-logo-placeholder" />}
          <div className="cotp-doc-box">
            <div className="cotp-doc-tipo">Cotización</div>
            <div className="cotp-doc-num">N°: {nroCorto}</div>
          </div>
        </div>

        {cot.estado === 'ANULADA' && <div className="cotp-anulada-marca">ANULADA</div>}

        {/* Caja empresa */}
        <div className="cotp-empresa-box">
          <div className="cotp-empresa-nombre">{empresa?.razon_social || 'EMPRESA'}</div>
          {empresa?.ruc && <div className="cotp-empresa-linea">RUC: {empresa.ruc}</div>}
          {empresa?.direccion && <div className="cotp-empresa-linea">{empresa.direccion}</div>}
          {empresa?.telefono && <div className="cotp-empresa-linea">Cel.: {empresa.telefono}</div>}
        </div>

        {/* Franja info */}
        <div className="cotp-info">
          <div className="cotp-info-row">
            <div className="cotp-info-item"><span className="cotp-info-key">Fecha:</span><span>{fmtFecha(cot.fecha)}</span></div>
            <div className="cotp-info-item"><span className="cotp-info-key">Fecha vencimiento:</span><span>{fmtFecha(cot.fecha_vencimiento)}</span></div>
            <div className="cotp-info-item"><span className="cotp-info-key">Nro. días:</span><span>{cot.dias_vigencia}</span></div>
          </div>
          <div className="cotp-info-row">
            <div className="cotp-info-item"><span className="cotp-info-key">Sr.(es)(a):</span><span>{cot.cliente_nombre || 'CLIENTE VARIOS'}</span></div>
            {cot.cliente_documento && (
              <div className="cotp-info-item"><span className="cotp-info-key">Doc.:</span><span>{cot.cliente_documento}</span></div>
            )}
          </div>
          {cot.cliente_direccion && (
            <div className="cotp-info-row">
              <div className="cotp-info-item"><span className="cotp-info-key">Dirección:</span><span>{cot.cliente_direccion}</span></div>
            </div>
          )}
          {cot.detalle && (
            <div className="cotp-info-row">
              <div className="cotp-info-item"><span className="cotp-info-key">Detalle:</span><span>{cot.detalle}</span></div>
            </div>
          )}
          <div className="cotp-info-row">
            <div className="cotp-info-item"><span className="cotp-info-key">Cotizador:</span><span>{(cot.usuario_nombre || '—').toUpperCase()}</span></div>
          </div>
        </div>

        {/* Tabla de productos */}
        <table className="cotp-tabla">
          <thead>
            <tr>
              <th style={{ width: 70 }}>Código</th>
              <th>Producto</th>
              <th style={{ width: 44 }}>Und</th>
              <th style={{ width: 64 }}>Cantidad</th>
              <th style={{ width: 76 }}>Precio Unit.</th>
              <th style={{ width: 84 }}>Precio Total</th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={l.id || i}>
                <td className="ctr">{l.codigo || '—'}</td>
                <td>{l.producto_nombre}</td>
                <td className="ctr">{l.unidad_short || 'UND'}</td>
                <td className="ctr">{parseFloat(l.cantidad)}</td>
                <td className="num">{fmt2(l.precio_unitario)}</td>
                <td className="num">{fmt2(l.subtotal)}</td>
              </tr>
            ))}
            {lineas.length < 6 &&
              Array.from({ length: 6 - lineas.length }).map((_, i) => (
                <tr key={`e-${i}`}>
                  <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Resumen: productos + totales */}
        <div className="cotp-resumen">
          <div className="cotp-productos">Productos: {lineas.length}</div>
          <div className="cotp-totales">
            {Number(cot.descuento) > 0 && (
              <>
                <div className="cotp-tot-row"><span>Subtotal</span><span>S/. {fmtMiles(cot.subtotal)}</span></div>
                <div className="cotp-tot-row"><span>Descuento</span><span>- S/. {fmtMiles(cot.descuento)}</span></div>
              </>
            )}
            {Number(cot.igv) > 0 && (
              <div className="cotp-tot-row"><span>IGV 18%</span><span>S/. {fmtMiles(cot.igv)}</span></div>
            )}
            <div className="cotp-tot-row final"><span>Total:</span><span>S/. {fmtMiles(cot.total)}</span></div>
          </div>
        </div>

        {/* V°B° */}
        <div className="cotp-vobo">
          <span className="cotp-vobo-line">V°B°</span>
        </div>

        {/* Pie de página */}
        <div className="cotp-footer">
          <div className="cotp-footer-col">
            <div className="cotp-footer-title">Condiciones Generales</div>
            <div>• Cotización válida por {cot.dias_vigencia} día(s).</div>
            <div>• Precios sujetos a variación sin previo aviso al vencer la vigencia.</div>
            <div>• No incluye traslado salvo indicación expresa.</div>
          </div>
          <div className="cotp-footer-col">
            <div className="cotp-footer-title">Contactos</div>
            {empresa?.telefono && <div>Celular: {empresa.telefono}</div>}
            {empresa?.correo && <div>Email: {empresa.correo}</div>}
            {empresa?.direccion && <div>{empresa.direccion}</div>}
          </div>
        </div>
      </div>
    </>
  );
}
